import { useState, useCallback, useRef } from "react";
import { Form } from "antd";
import { Id, toast } from "react-toastify";
import VLToastComponent from "views/components/VLToastComponent";
import {
  fetchExportStatusApiV1DatasetDatasetIdExportStatusGet,
  fetchPostFlywheelEvalReportHtmlAsyncApiV1FlywheelDatasetIdEvalReportHtmlAsyncPost,
} from "vl-api/vlComponents";
import type * as Schemas from "vl-api/vlSchemas";
import { downloadFile } from "helpers/utility/downloadFile";
import { extractFileNameFromUrl } from "../../helpers/utility/extractFileNameFromUrl";

const showToast = (node: React.ReactNode): Id => {
  return toast(node, {
    autoClose: false,
    closeButton: false,
    closeOnClick: false,
  });
};

export const extractPathAfterGroundTruths = (path: string): string => {
  if (!path?.trim()) return path;

  const trimmed = path.trim();

  const regex = /ground-truths\//i;
  const match = trimmed.match(regex);

  if (!match || match.index === undefined) return trimmed;

  const startIndex = match.index + match[0].length;
  return trimmed.slice(startIndex);
};

export const useEvaluateLabelPropagationModal = (datasetId?: string) => {
  const [isOpen, setIsOpen] = useState(false);
  const [form] = Form.useForm();

  const toastIdRef = useRef<Id | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const openModal = () => setIsOpen(true);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    form.resetFields();

    if (pollingIntervalRef.current) {
      clearTimeout(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, [form]);

  // ----------------- Toast Handlers -----------------
  const pendingNotification = useCallback((progress = 0) => {
    toastIdRef.current = showToast(
      <VLToastComponent
        status="IN_PROGRESS"
        progress={progress}
        title="Evaluating Label Propagation..."
        onClose={() => toast.dismiss(toastIdRef.current!)}
        helperText="This process will continue in the background. You can close this window"
        description="Your evaluation is almost ready! You can find your ZIP file in the 'Downloads' folder shortly."
      />,
    );
  }, []);

  const successNotification = useCallback((uri: string) => {
    toast.dismiss(toastIdRef.current!);

    toastIdRef.current = showToast(
      <VLToastComponent
        status="SUCCESS"
        title="The file is now ready!"
        onClose={() => toast.dismiss(toastIdRef.current!)}
        helperText={
          <span>
            You can download the file again by clicking this{" "}
            <a
              href={uri}
              download={extractFileNameFromUrl(uri)}
              rel="noopener noreferrer"
            >
              link
            </a>
            .
          </span>
        }
        description={
          "Your evaluation report is almost ready! You can find your ZIP file in the 'Downloads' folder shortly."
        }
      />,
    );
  }, []);

  const errorNotification = useCallback((msg: string) => {
    toast.dismiss(toastIdRef.current!);

    toastIdRef.current = showToast(
      <VLToastComponent
        status="ERROR"
        title="Oops! We could not process your evaluation"
        description={msg}
        onClose={() => toast.dismiss(toastIdRef.current!)}
      />,
    );
  }, []);

  // ----------------- Polling Logic -----------------
  const startPolling = useCallback(
    async (evaluationId: string) => {
      if (!datasetId) {
        errorNotification("Dataset ID is required.");
        return;
      }

      // Stop any existing polling
      if (pollingIntervalRef.current) {
        clearTimeout(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      pendingNotification(0);

      const poll = async () => {
        try {
          const response =
            await fetchExportStatusApiV1DatasetDatasetIdExportStatusGet({
              pathParams: { datasetId },
              queryParams: { export_task_id: evaluationId },
            });

          // The API response might be wrapped, so we check for data property
          const status: Schemas.ExportTask =
            (response as any)?.data || response;

          toast.update(toastIdRef.current!, {
            render: (
              <VLToastComponent
                status="IN_PROGRESS"
                progress={status.progress || 0}
                title="Evaluating Label Propagation..."
                onClose={() => toast.dismiss(toastIdRef.current!)}
                helperText="This process will continue in the background. You can close this window"
                description="Your evaluation report is almost ready! You can find your ZIP file in the 'Downloads' folder shortly."
              />
            ),
          });

          // ---- Stop Polling on Completion or Failure ----
          if (status.status === "COMPLETED") {
            if (status.download_uri) {
              successNotification(status.download_uri);
              downloadFile(extractFileNameFromUrl(status.download_uri), {
                type: "href",
                href: status.download_uri,
              });
              return; // <-- stop polling
            } else {
              errorNotification(
                "Export completed but no download URI available.",
              );
              return; // <-- stop polling
            }
          } else if (
            status.status === "FAILED" ||
            status.status === "REJECTED"
          ) {
            errorNotification(
              status.result_message ||
                "Export failed. Please try again or contact support.",
            );
            return; // <-- stop polling
          }

          pollingIntervalRef.current = setTimeout(poll, 2000);
        } catch (e: any) {
          errorNotification(e?.message || "Failed to check status.");
        }
      };

      poll(); // immediate first call
    },
    [datasetId, pendingNotification, successNotification, errorNotification],
  );

  const handleRunEvaluation = useCallback(async () => {
    if (!datasetId) {
      return errorNotification("Dataset ID is required.");
    }

    try {
      const formData = new FormData();

      const files = form.getFieldValue("files") || [];
      const dbCsvFile = files.find((f: File) =>
        f.name.toLowerCase().endsWith("db.csv"),
      );
      const classesJsonFile = files.find((f: File) =>
        f.name.toLowerCase().endsWith("classes.json"),
      );

      if (dbCsvFile) {
        formData.append("db_csv", dbCsvFile);
      }
      if (classesJsonFile) {
        formData.append("classes_json", classesJsonFile);
      }

      const response =
        await fetchPostFlywheelEvalReportHtmlAsyncApiV1FlywheelDatasetIdEvalReportHtmlAsyncPost(
          {
            pathParams: { datasetId },
            body: formData as any,
            headers: {
              "Content-Type": "multipart/form-data",
            },
          },
        );

      const taskId =
        typeof response.id === "string" ? response.id : String(response.id);
      startPolling(taskId);
    } catch (err: any) {
      errorNotification(
        err?.response?.detail || err?.message || "Failed to start evaluation.",
      );
    }
  }, [datasetId, startPolling, errorNotification, form]);

  return {
    isOpen,
    openModal,
    closeModal,
    handleRunEvaluation,
    form,
  };
};
